/* © CNES */
import { Chip } from 'primereact/chip';
import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { refDataProduct } from '../../../../../assets/config/filters/Referenciel';
import { ConfigContext } from '../../../../../context/ConfigContext';
import { FilterDataContext } from '../../../../../context/FilterDataContext';
import { ItemFilter } from '../../../../../models/Filter';
import {
  DATASET,
  PROCESSING_LEVEL,
  PRODUCT_TYPE,
} from '../../../../shared/Constants';
import { parseFilterContextToUrl } from '../../../../shared/Utils';
import './FilterDynamicList.scss';
import { getDynamicFilters, getUpdatedFilterData } from './Utils';

/**
 * FilterDynamicList Type
 */
type FilterDynamicListType = {
  setSelectedMissions: Function;
};

/**
 * Filter Dynamic List to display active filters Chips.
 * @returns FilterDynamicList Element.
 */
export const FilterDynamicList = ({
  setSelectedMissions,
}: FilterDynamicListType) => {
  const context = useContext(ConfigContext);
  const { filterData, setFilterData } = useContext(FilterDataContext);
  const navigate = useNavigate();
  const allDynamicFilter: { label: string; key: string }[] = useMemo(() => {
    return getDynamicFilters(
      filterData,
      context.mapExtent.currentView,
      context.searchZone,
      context.drawnShape
    );
  }, [filterData, context.mapExtent, context.searchZone, context.drawnShape]);

  /**
   * Remove a Chip from dynamic filters.
   * @param key the key from the filter to remove.
   */
  const onHandleRemove = (item: { label: string; key: string }) => {
    let urlParams = null;
    const { newFilterData, newMapExtent, selectedMission } =
      getUpdatedFilterData(filterData, context.mapExtent, item);
    switch (item.key) {
      case DATASET:
        setSelectedMissions(selectedMission);
        break;
      case 'dataProduct':
        removeProductDataItem(item, newFilterData);
        break;
      case 'SHP file':
      case 'GeoJSON file':
      case 'KML file':
        // timeout to allow multiple context set actions.
        setTimeout(() => {
          context.setSearchZone({ type: null, value: null });
        }, 0);
        break;
      case 'HandDrawnArea':
        context.setDrawnShape(undefined);
        break;
      case 'CurrentView':
        context.setMapExtent({
          ...newMapExtent,
        });
        break;
      default:
        setFilterData(newFilterData);
    }

    if (item.key === 'HandDrawnArea') {
      // timeout to allow multiple context set actions.
      setTimeout(() => {
        context.setDrawnShape(undefined);
      }, 0);
    }
    context.setMapExtent({
      ...newMapExtent,
    });
    setFilterData(newFilterData);
    urlParams = parseFilterContextToUrl(
      newFilterData,
      context.drawnShape,
      newMapExtent.currentView ? newMapExtent.extent : undefined,
      context.searchZone
    );
    navigate({
      pathname: urlParams ? '/search' : '/',
      ...(urlParams && { search: urlParams }),
    });
  };

  /**
   * remove ProductData Item.
   * @param item the item to remove.
   * @param newFilterData the state of the filter to mutate.
   * @returns newFilterData
   */
  const removeProductDataItem = (
    item: { label: string; key: string },
    newFilterData: ItemFilter
  ) => {
    const refDataProductRemoved = refDataProduct.find(
      (refDataProduct) => refDataProduct.key === item.label
    );
    const newProductTypes: string[] = newFilterData[PRODUCT_TYPE] as string[];
    const newProductLevels: string[] = newFilterData[
      PROCESSING_LEVEL
    ] as string[];
    if (refDataProductRemoved?.type === 'productType') {
      newFilterData[PRODUCT_TYPE] = newProductTypes.filter(
        (productTypeName) => productTypeName !== refDataProductRemoved.key
      );
      newFilterData[PROCESSING_LEVEL] = newProductLevels.filter(
        (productLevelName) =>
          !refDataProductRemoved.relation.includes(productLevelName)
      );
    } else if (refDataProductRemoved?.type === 'productLevel') {
      newFilterData[PRODUCT_TYPE] = newProductTypes.filter(
        (productTypeName) =>
          !refDataProductRemoved.relation.includes(productTypeName)
      );
      newFilterData[PROCESSING_LEVEL] = newProductLevels.filter(
        (productLevelName) => productLevelName !== refDataProductRemoved.key
      );
    }

    return newFilterData;
  };

  return (
    <div className="filterDynamic ChipsContainer">
      {allDynamicFilter.map((filter, index) => {
        return (
          <Chip
            className="CustomChip dynamicFilter"
            key={`${filter.label}-${index}`}
            label={filter.label}
            removeIcon="pi pi-times"
            removable
            onRemove={() => onHandleRemove(filter)}
          />
        );
      })}
    </div>
  );
};
